import _extends from "@babel/runtime/helpers/extends";
/** @jsx jsx */
import { useCallback, useMemo, useRef } from 'react';
import { jsx } from '@emotion/react';
import isEqual from 'lodash/isEqual';
import uuid from 'uuid/v4';
import { FabricEditorAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ACTION, fireAnalyticsEvent } from '@atlaskit/editor-common/analytics';
import { useConstructor } from '@atlaskit/editor-common/hooks';
import { EditorExperience, ExperienceStore } from '@atlaskit/editor-common/ufo';
import { getAnalyticsAppearance, startMeasure, stopMeasure } from '@atlaskit/editor-common/utils';
import EditorActions from '../actions';
import { createFeatureFlagsFromProps } from '../create-editor/feature-flags-from-props';
import { useEditorContext } from '../ui/EditorContext';
import measurements from '../utils/performance/measure-enum';
import { name, version } from '../version-wrapper';
import { EditorInternal } from './editor-internal';
import useMeasureEditorMountTime from './hooks/useMeasureEditorMountTime';
import useMemoEditorProps from './hooks/useMemoEditorProps';
import useProviderFactory from './hooks/useProviderFactory';
import sendDurationAnalytics from './utils/sendDurationAnalytics';
import trackEditorActions from './utils/trackEditorActions';

/**
 * Editor wrapper that deals with the lifecycle logic of the editor
 */
function Editor(passedProps) {
  var propsRef = useRef(passedProps);
  var props = useMemoEditorProps(passedProps);
  useMemo(function () {
    propsRef.current = props;
  }, [props]);
  var editorContext = useEditorContext();
  var editorActionsPlaceholderInstance = useMemo(function () {
    return new EditorActions();
  }, []);
  var editorActions = editorContext.editorActions || editorActionsPlaceholderInstance;
  var startTime = useRef(performance.now());
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var experienceStore = useRef();
  var handleAnalyticsEvent = useCallback(function (data) {
    fireAnalyticsEvent(createAnalyticsEvent)(data);
  }, [createAnalyticsEvent]);
  useConstructor(function () {
    trackEditorActions(editorActions, props.performanceTracking, function (value) {
      return handleAnalyticsEvent(value);
    });
  });
  var getExperienceStore = useCallback(function () {
    return experienceStore.current;
  }, []);
  var getFeatureFlagsFromRef = useCallback(function () {
    return createFeatureFlagsFromProps(propsRef.current);
  }, []);
  var onEditorCreated = useCallback(function (instance) {
    var _propsRef$current = propsRef.current,
      contextIdentifierProvider = _propsRef$current.contextIdentifierProvider,
      onEditorReady = _propsRef$current.onEditorReady,
      performanceTracking = _propsRef$current.performanceTracking,
      featureFlags = _propsRef$current.featureFlags;
    editorActions._privateRegisterEditor(instance.view, instance.eventDispatcher, instance.transformer, getFeatureFlagsFromRef);
    if (featureFlags !== null && featureFlags !== void 0 && featureFlags.ufo) {
      var _experienceStore$curr;
      experienceStore.current = ExperienceStore.getInstance(instance.view);
      (_experienceStore$curr = experienceStore.current) === null || _experienceStore$curr === void 0 || _experienceStore$curr.start(EditorExperience.loadEditor, startTime.current);
    }
    if (onEditorReady) {
      var _performanceTracking$;
      var measureEditorReady = (performanceTracking === null || performanceTracking === void 0 || (_performanceTracking$ = performanceTracking.onEditorReadyCallbackTracking) === null || _performanceTracking$ === void 0 ? void 0 : _performanceTracking$.enabled) || (featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.ufo);
      measureEditorReady && startMeasure(measurements.ON_EDITOR_READY_CALLBACK);
      onEditorReady(editorActions);
      measureEditorReady && stopMeasure(measurements.ON_EDITOR_READY_CALLBACK, sendDurationAnalytics(ACTION.ON_EDITOR_READY_CALLBACK, {
        contextIdentifierProvider: contextIdentifierProvider,
        featureFlags: featureFlags
      }, getExperienceStore, createAnalyticsEvent));
    }
  }, [editorActions, createAnalyticsEvent, getFeatureFlagsFromRef, propsRef, getExperienceStore]);
  var onEditorDestroyed = useCallback(function (_instance) {
    var onDestroy = propsRef.current.onDestroy;
    editorActions._privateUnregisterEditor();
    if (onDestroy) {
      onDestroy();
    }
  }, [editorActions, propsRef]);
  useMeasureEditorMountTime(props, getExperienceStore, createAnalyticsEvent);
  var providerFactory = useProviderFactory(props, editorActions, createAnalyticsEvent);
  var onSaveFromProps = props.onSave;
  var handleSave = useCallback(function (view) {
    if (onSaveFromProps) {
      onSaveFromProps(view);
    }
  }, [onSaveFromProps]);
  return jsx(EditorInternal, {
    props: props,
    handleAnalyticsEvent: handleAnalyticsEvent,
    createAnalyticsEvent: createAnalyticsEvent,
    preset: props.preset,
    handleSave: handleSave,
    editorActions: editorActions,
    onEditorCreated: onEditorCreated,
    onEditorDestroyed: onEditorDestroyed,
    providerFactory: providerFactory
  });
}
var useMemoEditorFeatureFlags = function useMemoEditorFeatureFlags(featureFlags) {
  var ffRef = useRef(featureFlags);
  if (!isEqual(ffRef.current, featureFlags)) {
    ffRef.current = featureFlags;
  }
  return ffRef.current;
};
export function ComposableEditor(props) {
  var editorSessionId = useRef(uuid());
  var data = useMemo(function () {
    return {
      packageName: name,
      packageVersion: version,
      componentName: 'editorCore',
      appearance: getAnalyticsAppearance(props.appearance),
      editorSessionId: editorSessionId.current
    };
  }, [props.appearance]);
  var memodEditorFeatureFlags = useMemoEditorFeatureFlags(props.featureFlags);
  return jsx(FabricEditorAnalyticsContext
  // @ts-expect-error Type 'string' is not assignable to type '"editorCore" | "renderer"'.
  , {
    data: data
  }, jsx(Editor, _extends({}, props, {
    featureFlags: memodEditorFeatureFlags
  })));
}
ComposableEditor.propTypes = {
  minHeight: function minHeight(_ref) {
    var appearance = _ref.appearance,
      _minHeight = _ref.minHeight;
    if (_minHeight && appearance && !['comment', 'chromeless'].includes(appearance)) {
      return new Error('minHeight only supports editor appearance chromeless and comment for Editor');
    }
    return null;
  }
};